export type ReportLocale = 'en' | 'sk' | 'cs';

const en = {
  language: 'Review language',
  policy:
    'Adult books remain allowed and searchable. A report is an allegation. Assess the actual content and context; profanity, nudity and mature themes are not automatically prohibited. Pornographic cover imagery is prohibited.',
  providerEdition: 'Unsaved provider edition',
  targets: { cover: 'Cover', description: 'Description', book: 'Book information' },
  reasons: {
    explicit_sexual: 'Explicit sexual imagery',
    offensive_text: 'Offensive text on the cover',
    graphic_violence: 'Graphic or gory imagery',
    wrong_artwork: 'Wrong artwork',
    offensive_language: 'Offensive language',
    hateful_content: 'Hateful or discriminatory content',
    graphic_content: 'Graphic sexual or violent content',
    inaccurate: 'Inaccurate description',
    metadata: 'Incorrect book details',
    duplicate: 'Duplicate book',
    wrong_edition: 'Wrong edition',
    inappropriate: 'Other inappropriate content',
    cover: 'Cover problem',
    rights: 'Rights or copyright concern',
  },
  states: {
    visible: 'Visible',
    held: 'Temporarily held for review',
    suppressed: 'Suppressed after review',
  },
  statuses: {
    pending: 'Pending',
    resolved: 'Resolved',
    dismissed: 'Dismissed',
    confirmed: 'Violation confirmed',
    replaced: 'Replaced',
  },
  details: 'Reader explanation',
  evidence: 'New evidence',
  replacement: 'Published replacement',
  report: 'Report',
  version: 'Report version',
  contentVersion: 'Content revision',
  versionKey: 'Reported version',
  identity: 'Provider and content identity',
  reportedContent: 'Inspect reported content',
  noContent: 'No content snapshot is available for this report.',
  unsafeUrl: 'This image cannot be previewed safely. Inspect its recorded identity.',
  coverAlt: 'Reported cover — moderator inspection',
  snapshot: 'This is the reported version, available only to authorized moderators.',
  mutableUrl:
    'A provider may change image bytes at the same URL. This reference does not prove the image is unchanged.',
  suggestedDuplicate: 'Suggested duplicate',
  resolution: 'Resolution',
  related: 'Related reports for this content',
  noRelated: 'No other reports for this content.',
  relatedTruncated:
    'Only some related reports are shown. Review and resolve reports individually; group decisions are unavailable until the whole group can be reviewed.',
  relatedGroup: 'Apply this decision to all related pending reports for this content version',
  relatedHelp:
    'Review their reasons and explanations first. This explicitly resolves the group in the same transaction.',
  singleHelp:
    'Dismissing one report does not release other pending holds or an existing confirmed suppression.',
  reason: 'Moderation reason',
  reasonHint: 'Explain the content and context that support this decision.',
  decision: 'Decision',
  dismiss: 'Dismiss allegation',
  suppress: 'Confirm violation and suppress',
  replace: 'Replace reported content',
  resolve: 'Resolve information issue',
  dismissEffect:
    'Close this issue as allowed content. It becomes visible only when no other unresolved hold or confirmed suppression applies.',
  suppressEffect:
    'Confirm a violation for this version and keep it suppressed, including when a provider returns it again. The book remains searchable.',
  replaceEffect:
    'Suppress the reported version and publish the replacement in the same transaction. The other reported field keeps its own outcome.',
  resolveEffect: 'Close this information issue without hiding the book, cover or description.',
  replaceDescription: 'Replacement description',
  save: 'Save this decision',
  saving: 'Saving decision…',
  saved: 'Moderation decision saved. The queue now shows the latest state.',
  reload: 'Reload reports',
  stale: 'This report or content changed. Reload and review the latest version before deciding.',
  failed:
    'The decision could not be saved. Your edits are retained. Retry the same decision or reload.',
  legacy:
    'This report needs the updated moderation API before a decision can be made. Reload after the API and migration are available.',
  pendingVersion:
    'The reported content version is unavailable. Reload before making a content decision.',
  replacementRequired:
    'Choose a different, loaded HTTPS cover or enter a changed description before saving.',
};
type Copy = {
  [K in keyof typeof en]: (typeof en)[K] extends Record<string, string>
    ? { [P in keyof (typeof en)[K]]: string }
    : string;
};
const sk: Copy = {
  relatedTruncated:
    'Zobrazuje sa len časť súvisiacich nahlásení. Posúďte a vyriešte ich jednotlivo; skupinové rozhodnutie nie je dostupné, kým nemožno posúdiť celú skupinu.',
  evidence: 'Nové dôkazy',
  replacement: 'Zverejnená náhrada',
  language: 'Jazyk kontroly',
  policy:
    'Knihy pre dospelých sú povolené a zostávajú vo vyhľadávaní. Nahlásenie je podnet, nie dôkaz. Posúďte obsah aj kontext; vulgarizmy, nahota a témy pre dospelých nie sú automaticky zakázané. Pornografické obrázky na obálkach sú zakázané.',
  providerEdition: 'Neuložené vydanie od poskytovateľa',
  targets: { cover: 'Obálka', description: 'Popis', book: 'Údaje o knihe' },
  reasons: {
    explicit_sexual: 'Explicitné sexuálne obrázky',
    offensive_text: 'Urážlivý text na obálke',
    graphic_violence: 'Brutálne alebo krvavé obrázky',
    wrong_artwork: 'Nesprávna obálka',
    offensive_language: 'Urážlivý jazyk',
    hateful_content: 'Nenávistný alebo diskriminačný obsah',
    graphic_content: 'Explicitný sexuálny alebo násilný obsah',
    inaccurate: 'Nepresný popis',
    metadata: 'Nesprávne údaje o knihe',
    duplicate: 'Duplicitná kniha',
    wrong_edition: 'Nesprávne vydanie',
    inappropriate: 'Iný nevhodný obsah',
    cover: 'Problém s obálkou',
    rights: 'Práva alebo autorské práva',
  },
  states: {
    visible: 'Viditeľné',
    held: 'Dočasne skryté počas kontroly',
    suppressed: 'Skryté po kontrole',
  },
  statuses: {
    pending: 'Čaká na kontrolu',
    resolved: 'Vyriešené',
    dismissed: 'Zamietnuté',
    confirmed: 'Porušenie potvrdené',
    replaced: 'Nahradené',
  },
  details: 'Vysvetlenie čitateľa',
  report: 'Nahlásenie',
  version: 'Verzia nahlásenia',
  contentVersion: 'Revízia obsahu',
  versionKey: 'Nahlásená verzia',
  identity: 'Identita poskytovateľa a obsahu',
  reportedContent: 'Zobraziť nahlásený obsah',
  noContent: 'Pre toto nahlásenie nie je dostupná snímka obsahu.',
  unsafeUrl: 'Tento obrázok sa nedá bezpečne zobraziť. Skontrolujte zaznamenanú identitu.',
  coverAlt: 'Nahlásená obálka – kontrola moderátorom',
  snapshot: 'Toto je nahlásená verzia dostupná len oprávneným moderátorom.',
  mutableUrl:
    'Poskytovateľ môže zmeniť obrázok na rovnakej adrese. Tento odkaz nedokazuje, že obrázok zostal rovnaký.',
  suggestedDuplicate: 'Navrhovaný duplikát',
  resolution: 'Výsledok',
  related: 'Súvisiace nahlásenia tohto obsahu',
  noRelated: 'Žiadne ďalšie nahlásenia tohto obsahu.',
  relatedGroup: 'Použiť rozhodnutie na všetky súvisiace nevyriešené nahlásenia tejto verzie obsahu',
  relatedHelp:
    'Najprv posúďte ich dôvody a vysvetlenia. Celá skupina sa výslovne vyrieši v rovnakej transakcii.',
  singleHelp:
    'Zamietnutie jedného nahlásenia nezruší ostatné nevyriešené obmedzenia ani potvrdené potlačenie.',
  reason: 'Dôvod rozhodnutia',
  reasonHint: 'Vysvetlite obsah a kontext, o ktoré sa rozhodnutie opiera.',
  decision: 'Rozhodnutie',
  dismiss: 'Zamietnuť podnet',
  suppress: 'Potvrdiť porušenie a skryť',
  replace: 'Nahradiť nahlásený obsah',
  resolve: 'Vyriešiť problém s údajmi',
  dismissEffect:
    'Uzavrieť tento podnet ako povolený obsah. Obsah sa zobrazí, iba ak sa naň nevzťahuje iné nevyriešené alebo potvrdené obmedzenie.',
  suppressEffect:
    'Potvrdiť porušenie pre túto verziu a ponechať ju skrytú aj pri opätovnom vrátení poskytovateľom. Kniha zostane vo vyhľadávaní.',
  replaceEffect:
    'Skryť nahlásenú verziu a zverejniť náhradu v rovnakej transakcii. Druhá nahlásená časť si zachová vlastný výsledok.',
  resolveEffect: 'Uzavrieť problém s údajmi bez skrytia knihy, obálky či popisu.',
  replaceDescription: 'Náhradný popis',
  save: 'Uložiť rozhodnutie',
  saving: 'Ukladanie rozhodnutia…',
  saved: 'Rozhodnutie je uložené. Zoznam zobrazuje aktuálny stav.',
  reload: 'Načítať nahlásenia znova',
  stale: 'Nahlásenie alebo obsah sa zmenili. Načítajte a posúďte aktuálnu verziu.',
  failed:
    'Rozhodnutie sa nepodarilo uložiť. Úpravy zostali zachované. Skúste rovnaké rozhodnutie znova alebo načítajte údaje.',
  legacy:
    'Toto nahlásenie vyžaduje aktualizované API moderovania. Načítajte ho znova po sprístupnení API a migrácie.',
  pendingVersion:
    'Verzia nahláseného obsahu nie je dostupná. Pred rozhodnutím načítajte údaje znova.',
  replacementRequired:
    'Pred uložením vyberte inú načítanú HTTPS obálku alebo zadajte upravený popis.',
};
const cs: Copy = {
  relatedTruncated:
    'Zobrazuje se pouze část souvisejících nahlášení. Posuďte a vyřešte je jednotlivě; skupinové rozhodnutí není dostupné, dokud nelze posoudit celou skupinu.',
  evidence: 'Nové důkazy',
  replacement: 'Zveřejněná náhrada',
  language: 'Jazyk kontroly',
  policy:
    'Knihy pro dospělé jsou povolené a zůstávají ve vyhledávání. Nahlášení je podnět, ne důkaz. Posuďte obsah i kontext; vulgarismy, nahota a témata pro dospělé nejsou automaticky zakázané. Pornografické obrázky na obálkách jsou zakázané.',
  providerEdition: 'Neuložené vydání od poskytovatele',
  targets: { cover: 'Obálka', description: 'Popis', book: 'Údaje o knize' },
  reasons: {
    explicit_sexual: 'Explicitní sexuální obrázky',
    offensive_text: 'Urážlivý text na obálce',
    graphic_violence: 'Brutální nebo krvavé obrázky',
    wrong_artwork: 'Nesprávná obálka',
    offensive_language: 'Urážlivý jazyk',
    hateful_content: 'Nenávistný nebo diskriminační obsah',
    graphic_content: 'Explicitní sexuální nebo násilný obsah',
    inaccurate: 'Nepřesný popis',
    metadata: 'Nesprávné údaje o knize',
    duplicate: 'Duplicitní kniha',
    wrong_edition: 'Nesprávné vydání',
    inappropriate: 'Jiný nevhodný obsah',
    cover: 'Problém s obálkou',
    rights: 'Práva nebo autorská práva',
  },
  states: {
    visible: 'Viditelné',
    held: 'Dočasně skryté během kontroly',
    suppressed: 'Skryté po kontrole',
  },
  statuses: {
    pending: 'Čeká na kontrolu',
    resolved: 'Vyřešené',
    dismissed: 'Zamítnuté',
    confirmed: 'Porušení potvrzeno',
    replaced: 'Nahrazené',
  },
  details: 'Vysvětlení čtenáře',
  report: 'Nahlášení',
  version: 'Verze nahlášení',
  contentVersion: 'Revize obsahu',
  versionKey: 'Nahlášená verze',
  identity: 'Identita poskytovatele a obsahu',
  reportedContent: 'Zobrazit nahlášený obsah',
  noContent: 'Pro toto nahlášení není dostupný snímek obsahu.',
  unsafeUrl: 'Tento obrázek nelze bezpečně zobrazit. Zkontrolujte zaznamenanou identitu.',
  coverAlt: 'Nahlášená obálka – kontrola moderátorem',
  snapshot: 'Toto je nahlášená verze dostupná pouze oprávněným moderátorům.',
  mutableUrl:
    'Poskytovatel může změnit obrázek na stejné adrese. Tento odkaz nedokazuje, že obrázek zůstal stejný.',
  suggestedDuplicate: 'Navrhovaný duplikát',
  resolution: 'Výsledek',
  related: 'Související nahlášení tohoto obsahu',
  noRelated: 'Žádná další nahlášení tohoto obsahu.',
  relatedGroup: 'Použít rozhodnutí na všechna související nevyřešená nahlášení této verze obsahu',
  relatedHelp:
    'Nejprve posuďte jejich důvody a vysvětlení. Celá skupina se výslovně vyřeší ve stejné transakci.',
  singleHelp:
    'Zamítnutí jednoho nahlášení nezruší ostatní nevyřešená omezení ani potvrzené potlačení.',
  reason: 'Důvod rozhodnutí',
  reasonHint: 'Vysvětlete obsah a kontext, o které se rozhodnutí opírá.',
  decision: 'Rozhodnutí',
  dismiss: 'Zamítnout podnět',
  suppress: 'Potvrdit porušení a skrýt',
  replace: 'Nahradit nahlášený obsah',
  resolve: 'Vyřešit problém s údaji',
  dismissEffect:
    'Uzavřít tento podnět jako povolený obsah. Obsah se zobrazí, pouze pokud se na něj nevztahuje jiné nevyřešené nebo potvrzené omezení.',
  suppressEffect:
    'Potvrdit porušení této verze a ponechat ji skrytou i při opětovném vrácení poskytovatelem. Kniha zůstane ve vyhledávání.',
  replaceEffect:
    'Skrýt nahlášenou verzi a zveřejnit náhradu ve stejné transakci. Druhá nahlášená část si zachová vlastní výsledek.',
  resolveEffect: 'Uzavřít problém s údaji bez skrytí knihy, obálky či popisu.',
  replaceDescription: 'Náhradní popis',
  save: 'Uložit rozhodnutí',
  saving: 'Ukládání rozhodnutí…',
  saved: 'Rozhodnutí je uložené. Seznam zobrazuje aktuální stav.',
  reload: 'Načíst nahlášení znovu',
  stale: 'Nahlášení nebo obsah se změnily. Načtěte a posuďte aktuální verzi.',
  failed:
    'Rozhodnutí se nepodařilo uložit. Úpravy zůstaly zachované. Zkuste stejné rozhodnutí znovu nebo načtěte údaje.',
  legacy:
    'Toto nahlášení vyžaduje aktualizované API moderování. Načtěte ho znovu po zpřístupnění API a migrace.',
  pendingVersion: 'Verze nahlášeného obsahu není dostupná. Před rozhodnutím načtěte údaje znovu.',
  replacementRequired:
    'Před uložením vyberte jinou načtenou HTTPS obálku nebo zadejte upravený popis.',
};

export const REPORT_COPY: Record<ReportLocale, Copy> = { en, sk, cs };
export function reportLocale(
  language = typeof navigator === 'undefined' ? 'en' : navigator.language,
): ReportLocale {
  const locale = language.split('-')[0];
  return locale === 'sk' || locale === 'cs' ? locale : 'en';
}
export function reportReasonLabel(reason: string, locale: ReportLocale) {
  return (
    REPORT_COPY[locale].reasons[reason as keyof Copy['reasons']] ?? reason.replaceAll('_', ' ')
  );
}
